import { AttributeType } from "@/geoMap/define/AttributeType";
import { CqlParameter } from "@/geoMap/net/CqlParameter";
import { ETCDrmFifteenMinSearchDateAttributeConvert } from "@/attribute/convert/ETCDrmFifteenMinSearchDateAttributeConvert";
import { ETCDrmHourSearchDateAttributeConvert } from "@/attribute/convert/ETCDrmHourSearchDateAttributeConvert";
import { ETCDrmMonthSearchDateAttributeConvert } from "@/attribute/convert/ETCDrmMonthSearchDateAttributeConvert";
import { ETCLayerName, RoadType } from "./ETCLayerName";
import { ETCLayerNameMonth, DayType } from "./ETCLayerNameMonth";
import { ETCLinkFifteenMinSearchDateAttributeConvert } from "@/attribute/convert/ETCLinkFifteenMinSearchDateAttributeConvert";
import { ETCLinkHourSearchDateAttributeConvert } from "@/attribute/convert/ETCLinkHourSearchDateAttributeConvert";
import { ETCLinkMonthSearchDateAttributeConvert } from "@/attribute/convert/ETCLinkMonthSearchDateAttributeConvert";
import { Feature, Map } from "ol";
import { FeatureGeoJson } from "../FeatureGeoJson";
import GeoJSON from "ol/format/GeoJSON";
import { ILayerFactory } from "./ILayerFactory";
import ImageLayer from "ol/layer/Image";
import { ISearchDateAttributeConvert } from "@/attribute/convert/ISearchDateAttributeConvert";
import { MapContextMenuItem } from "../MapContextMenuItem";
import { SelectedFeatureInfo } from "../SelectedFeatureInfo";
import { WMSGetFeatureManager } from "../net/WMSGetFeatureManager";
import ImageWMSEx from "../net/ImageWMSEx";
import { WFSGetFeatureManager } from "../net/WFSGetFeatureManager";
import { LayerUtil } from "../util/LayerUtil"

/**
 * 最大ズームレベル
 */

const LAYER_MAX_ZOOMLEVEL = 18;

/**
 * 最小ズームレベル
 */

const LAYER_MIN_ZOOMLEVEL = 11;

/**
 * API通信用URL
 */
const URL_API = process.env.VUE_APP_XROAD_API_URL;

/**
 * レイヤ毎コンバータ情報
 */
type SearchDateAttributeConvertInfo = {
  /**
   * コンテキストメニュー名称
   */
  contextMenu: string;

  /**
   * Attributeコンバータ
   */
  attributeConvert: ISearchDateAttributeConvert;

  /**
   * 選択時スタイル
   */
  selectedLayerStyle: string;
};

/**
 * 過去属性変換クラス取得関数
 */
const FUNCTION_getSearchDateAttributeConvert = (
  key: string,
  geoJSON: any,
  layerName: string,
  roadType: RoadType,
  timeCode: string,
  dayType: DayType | null
): SearchDateAttributeConvertInfo => {
  switch (key) {
    /** ETC_DRM区間_15分 */
    case "t_etc_probe_drm_lnk_15m":
      return {
        contextMenu: "ETC2.0_確定値_DRMリンク_日別15分単位",
        attributeConvert: new ETCDrmFifteenMinSearchDateAttributeConvert(
          geoJSON,
          layerName,
          roadType,
          timeCode
        ),
        selectedLayerStyle: "t_etc_probe_drm_lnk_selected",
      };
    /** ETC_DRM区間_時間 */
    case "t_etc_probe_drm_lnk_1h":
      return {
        contextMenu: "ETC2.0_確定値_DRMリンク_日別時間帯別",
        attributeConvert: new ETCDrmHourSearchDateAttributeConvert(
          geoJSON,
          layerName,
          roadType,
          timeCode
        ),
        selectedLayerStyle: "t_etc_probe_drm_lnk_selected",
      };
    /** ETC_DRM区間_月 */
    case "t_etc_probe_drm_lnk_1m":
      return {
        contextMenu: "ETC2.0_確定値_DRMリンク_月別時間帯別",
        attributeConvert: new ETCDrmMonthSearchDateAttributeConvert(
          geoJSON,
          layerName,
          roadType,
          timeCode,
          dayType
        ),
        selectedLayerStyle: "t_etc_probe_drm_lnk_selected",
      };
    /** ETC_交通調査基本区間_15分 */
    case "t_etc_probe_traffic_15m":
      return {
        contextMenu: "ETC2.0_確定値_交通調査基本区間_日別15分単位",
        attributeConvert: new ETCLinkFifteenMinSearchDateAttributeConvert(
          geoJSON,
          layerName,
          roadType,
          timeCode
        ),
        selectedLayerStyle: "t_etc_probe_traffic_selected",
      };
    /** ETC_交通調査基本区間_時間 */
    case "t_etc_probe_traffic_1h":
      return {
        contextMenu: "ETC2.0_確定値_交通調査基本区間_日別時間帯別",
        attributeConvert: new ETCLinkHourSearchDateAttributeConvert(
          geoJSON,
          layerName,
          roadType,
          timeCode
        ),
        selectedLayerStyle: "t_etc_probe_traffic_selected",
      };
    /** ETC_交通調査基本区間_月 */
    case "t_etc_probe_traffic_1m":
      return {
        contextMenu: "ETC2.0_確定値_交通調査基本区間_月別時間帯別",
        attributeConvert: new ETCLinkMonthSearchDateAttributeConvert(
          geoJSON,
          layerName,
          roadType,
          timeCode,
          dayType
        ),
        selectedLayerStyle: "t_etc_probe_traffic_selected",
      };
  }
};

/**
 * ETC2.0用レイヤFactoryクラス
 */
export class ETCLayerFactory implements ILayerFactory {
  /**
   * 表示WMSレイヤ
   */
  private _wmsLayer: ImageLayer<ImageWMSEx>;

  /**
   * 表示WMSソース
   */
  private _wmsImageSource: ImageWMSEx;

  /**
   * 表示対象ETCレイヤ
   */
  private _visibleETCLayers: Array<ETCLayerName | ETCLayerNameMonth>;

  /**
   * 地物属性の有無を取得
   */
  isFeatureInfo(): boolean {
    return true;
  }

  /**
   * 範囲地物属性の有無を取得
   */
  isRangeFeatureInfo(): boolean {
    return false;
  }

  /**
   * 右クリック処理の有無を取得
   */
  isRightClickInfo(): boolean {
    return false;
  }

  /**
   * レイヤ定義を取得
   * @param _map openLayers Mapクラス
   */
  getViewLayer(_map?: Map): ImageLayer<ImageWMSEx>[] {
    //WMSレイヤの作成
    this._wmsImageSource = new ImageWMSEx({
      url: URL_API,
      params: {
        LAYERS: null,
        EXCEPTIONS: "application/json",
        CQL_FILTER: null,
      },
      ratio: 1,
      serverType: "geoserver",
      crossOrigin: "Anonymous",
    });
    this._wmsLayer = new ImageLayer({
      source: this._wmsImageSource,
      maxZoom: LAYER_MAX_ZOOMLEVEL,
      minZoom: LAYER_MIN_ZOOMLEVEL,
      visible: false,
    });

    return [this._wmsLayer];
  }

  /**
   * 地物情報を取得
   * @param _map 地図
   * @param _latitude クリック座標緯度
   * @param _longitude クリック座標経度
   * @param x クリック位置X
   * @param y クリック位置Y
   * @param width 地図幅
   * @param height 地図高さ
   * @param bBox 地図範囲（BBOX)
   */
  async getFeatureInfo(
    _map: Map,
    _latitude: number,
    _longitude: number,
    x: number,
    y: number,
    width: number,
    height: number,
    bBox: number[]
  ): Promise<SelectedFeatureInfo[]> {
    if (
      _map.getView().getZoom() > LAYER_MAX_ZOOMLEVEL ||
      _map.getView().getZoom() <= LAYER_MIN_ZOOMLEVEL
    ) {
      return null;
    }
    // 表示対象ETCLayerNameまたはETCLayerNameMonthからcqlパラメータクラスを作成
    const cqlParameters = this._visibleETCLayers.map((layerDetail) => {
      return new CqlParameter(
        layerDetail.getLayerName(),
        layerDetail.getCqlFilterString()
      );
    });
    const WMSGetter = new WMSGetFeatureManager();
    const selectedFeatureGeoJSON = await WMSGetter.getInfo(
      URL_API,
      bBox.join(","),
      cqlParameters,
      x,
      y,
      height,
      width,
      5
    ).catch((error: any) => {
      throw error;
    });
    const selectedFeatures = new GeoJSON().readFeatures(selectedFeatureGeoJSON);
    if (selectedFeatures.length === 0) return null;
    const selectFeatureInfoList: SelectedFeatureInfo[] = await Promise.all(
      selectedFeatures.map(async (feature) => {
        const id = feature.getId().toString();
        const layerKey = id.split(".")[0];

        // 選択地物に設定するidを作成
        let featureId = "";
        const roadType = feature.get("道路種別コード");
        if (layerKey.includes("traffic")) {
          const trafficSurveyBasicsSecionNum =
            feature.get("交通調査基本区間番号");
          const upDownCode = feature.get("上り・下りコード");
          featureId = `${layerKey}-${roadType}-${trafficSurveyBasicsSecionNum}-${
            upDownCode ?? "NA"
          }`;
        } else {
          const meshCodeSecond = feature.get("２次メッシュコード");
          const inflowNode = feature.get("流入ノード");
          const outflowNode = feature.get("流出ノード");
          featureId = `${layerKey}-${roadType}-${meshCodeSecond}-${inflowNode}-${outflowNode}`;
        }

        const baseCqlPArameterString = this._visibleETCLayers
          .find(
            (ele) =>
              ele.getLayerName() === layerKey &&
              ele.roadType === roadType.toString()
          )
          .getCqlFilterString();

        const wfsCqlParameterString = layerKey.includes("traffic")
          ? `${baseCqlPArameterString} AND "休平日区分"=${feature.get(
              "休平日区分"
            )}  AND "交通調査基本区間番号"='${feature.get(
              "交通調査基本区間番号"
            )}' AND "上り・下りコード"=${feature.get("上り・下りコード")}`
          : `${baseCqlPArameterString} AND "２次メッシュコード"=${feature.get(
              "２次メッシュコード"
            )} AND "流入ノード"='${feature.get(
              "流入ノード"
            )}' AND "流出ノード"='${feature.get("流出ノード")}'`;

        const wfsCqlParameters = [
          new CqlParameter(layerKey, wfsCqlParameterString),
        ];

        const WFSGetter = new WFSGetFeatureManager();
        const wfsGeoJson = await WFSGetter.getInfo(
          URL_API,
          wfsCqlParameters,
          null,
          feature.getId()
        ).catch((error) => {
          throw error;
        });
        const wfsFeature = new GeoJSON().readFeatures(wfsGeoJson)[0];
        if (!wfsFeature) return;

        //属性変換クラス・attributeクラスを生成
        let attributeConvertInfo: SearchDateAttributeConvertInfo = null;
        let attributeType: AttributeType = null;

        //月の時のみ平休日区分を設定し、idにも追加
        let dayType = null;
        if (
          layerKey === "t_etc_probe_drm_lnk_1m" ||
          layerKey === "t_etc_probe_traffic_1m"
        ) {
          dayType = feature.get("休平日区分");
          featureId = featureId + "-" + dayType;
        }

        // 過去属性変換(本番用)
        attributeConvertInfo = FUNCTION_getSearchDateAttributeConvert(
          layerKey,
          wfsGeoJson,
          layerKey,
          feature.get("道路種別コード"),
          String(feature.get("時間コード")),
          dayType
        );

        attributeType = AttributeType.History;

        // selectFeatureを生成（地物情報のみ）
        const geometry = wfsFeature.getGeometry();
        const selectFeature = new Feature({
          geometry,
        });
        selectFeature.setId(featureId);
        // featureGeoJSONを生成
        const geoJSON = new GeoJSON().writeFeature(wfsFeature);
        const featureGeoJSON = new FeatureGeoJson(geoJSON, id);
        // selectedFeatureInfoを生成して、リストに追加
        const selectedFeatureInfo = new SelectedFeatureInfo(
          selectFeature,
          attributeConvertInfo.contextMenu,
          featureGeoJSON,
          attributeType,
          attributeConvertInfo.attributeConvert as ISearchDateAttributeConvert,
          true,
          layerKey,
          attributeConvertInfo.selectedLayerStyle,
          id
        );
        return selectedFeatureInfo;
      })
    );
    return selectFeatureInfoList;
  }

  /**
   * 右クリック処理
   * @param map 地図
   * @param latitude クリック座標緯度
   * @param longitude クリック座標経度
   * @param x クリック位置X
   * @param y クリック位置Y
   * @param width 地図幅
   * @param height 地図高さ
   * @param bBox 地図範囲（BBOX)
   */
  async getRightClickInfo(
    _map: Map,
    _latitude: number,
    _longitude: number,
    _x: number,
    _y: number,
    _width: number,
    _height: number,
    _bBox: number[]
  ): Promise<MapContextMenuItem[]> {
    return null;
  }

  /**
   * 指定範囲の地物を取得
   * @param _map 地図
   * @param bBox 地図範囲（BBOX)
   */
  async getRangeFeatureInfo(
    _map: Map,
    bBox: number[]
  ): Promise<SelectedFeatureInfo[]> {
    if (
      _map.getView().getZoom() > LAYER_MAX_ZOOMLEVEL ||
      _map.getView().getZoom() <= LAYER_MIN_ZOOMLEVEL
    ) {
      return null;
    }
    // 表示対象ETCLayerNameまたはETCLayerNameMonthからcqlパラメータクラスを作成
    const cqlParameters = this._visibleETCLayers.map((layerDetail) => {
      return new CqlParameter(
        layerDetail.getLayerName(),
        layerDetail.getCqlFilterString()
      );
    });

    const WFSGetter = new WFSGetFeatureManager();
    const selectedFeatureGeoJSON = await WFSGetter.getInfo(
      URL_API,
      cqlParameters,
      bBox
    ).catch((error) => {
      throw error;
    });

    const selectedFeatures = new GeoJSON().readFeatures(selectedFeatureGeoJSON);
    if (selectedFeatures.length === 0) return null;
    const selectFeatureInfoList: SelectedFeatureInfo[] = [];
    selectedFeatures.forEach((feature) => {
      const id = feature.getId().toString();
      const layerKey = id.split(".")[0];

      //属性変換クラス・attributeクラスを生成
      let attributeConvertInfo: SearchDateAttributeConvertInfo = null;
      let attributeType: AttributeType = null;

      //月の時のみ平休日区分を設定
      let dayType = null;
      if (
        layerKey === "t_etc_probe_drm_lnk_1M" ||
        layerKey === "t_etc_probe_traffic_1M"
      ) {
        dayType = feature.get("休平日区分");
      }

      //AttributeConvertクラス内の実装が不十分なため一旦コメントアウト
      const selecetedGeoJSON = new GeoJSON().writeFeature(feature);
      attributeConvertInfo = FUNCTION_getSearchDateAttributeConvert(
        layerKey,
        selecetedGeoJSON,
        layerKey,
        feature.get("道路種別コード"),
        String(feature.get("時間コード")),
        dayType
      );

      attributeType = AttributeType.History;

      // selectFeatureを生成（地物情報のみ）
      const geometry = feature.getGeometry();
      const selectFeature = new Feature({
        geometry,
      });

      // featureGeoJSONを生成
      const geoJSON = new GeoJSON().writeFeature(feature);
      const featureGeoJSON = new FeatureGeoJson(geoJSON, id);
      // selectedFeatureInfoを生成して、リストに追加
      const selectedFeatureInfo = new SelectedFeatureInfo(
        selectFeature,
        attributeConvertInfo.contextMenu,
        featureGeoJSON,
        attributeType,
        attributeConvertInfo.attributeConvert as ISearchDateAttributeConvert
      );
      selectFeatureInfoList.push(selectedFeatureInfo);
    });
    return selectFeatureInfoList;
  }

  /**
   * レイヤの表示状態を設定
   * @param isVisible 表示有無
   * @param visibleLayerList 対象ETCLayerリスト
   */
  setVisible(
    isVisible: boolean,
    visibleLayerList: Array<ETCLayerName | ETCLayerNameMonth>
  ) {
    const layerKeys: string[] = [];
    const cqlParameters: string[] = [];
    if (isVisible) {
      this._visibleETCLayers = visibleLayerList;
      // 表示中のETCLayerNameもしくはETCLayerNameMonthからレイヤ名、条件を抽出
      if (Object.keys(this._visibleETCLayers).length) {
        this._visibleETCLayers.forEach((layerDetail) => {
          //レイヤ名取得
          const layerName = layerDetail.getLayerName();
          //CQLフィルタ条件取得
          const cqlParameterString = layerDetail.getCqlFilterString();

          layerKeys.push(layerName);
          cqlParameters.push(cqlParameterString);
        });

        this._wmsImageSource.updateParams({
          LAYERS: layerKeys.join(","),
          CQL_FILTER: cqlParameters.join(";"),
        });
        this._wmsImageSource.refresh();
        this._wmsLayer.setVisible(true);
      } else {
        this._wmsLayer.setVisible(false);
      }
    } else {
      this._wmsLayer.setVisible(false);
    }
  }

  /**
   * 表示中のレイヤ数を取得
   */
  getVisibleLayerCount() {
    let layerCount = 0;
    if (this._wmsLayer?.getVisible()) {
      layerCount = [
        ...new Set(
          this._visibleETCLayers.map((ele) => {
            ele.etcLayerType, ele.etcTimeType;
          })
        ),
      ].length;
    }
    return layerCount;
  }
  /**
   * 表示レイヤが存在するか判定
   */
   isVisibleLayer(_map:Map) {
    return LayerUtil.isVisibleLayer(_map, this._wmsLayer)
  }
}
